import { useMutation } from "@apollo/client";

import { Alert, Box, Button, Fade, Snackbar } from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { GRAPHQL_MUTATION_EMAIL_SUBSCRIBE } from "app/queries";
import React, { useEffect } from "react";
import analytics from "app/analytics/analytics";
import { useLocation } from "react-router";
import makeStyles from "@mui/styles/makeStyles";
import { FormInputText } from "./FormInputText";

const useStyles = makeStyles((theme) => ({
    newsletterTextField: {
        margin: "0 auto",
        marginBottom: theme.spacing(3),
    },
}));

type EmailSubscribeProps = {
    emailListId: string;
    placement: string;
    dark?: boolean;
    classes?: Partial<ReturnType<typeof useStyles>>;
};

export const EmailSubscribe = (props: EmailSubscribeProps) => {
    const { emailListId, placement, dark } = props;
    const [showError, setShowError] = React.useState(false);
    const [showSuccess, setShowSuccess] = React.useState(false);
    const classes = useStyles(props);
    const pageLocation = useLocation();
    const [emailSubscribeMutation, { loading: isSaving, error: saveError }] = useMutation(
        GRAPHQL_MUTATION_EMAIL_SUBSCRIBE,
    );

    const emailSubscribeSchema = z.object({
        email: z
            .string()
            .min(1, "Please complete this field")
            .email("Please enter a valid email address"),
    });
    type EmailSubscribeInput = z.TypeOf<typeof emailSubscribeSchema>;

    const {
        control,
        formState: { /* errors, */ isSubmitSuccessful },
        reset,
        handleSubmit,
    } = useForm<EmailSubscribeInput>({
        resolver: zodResolver(emailSubscribeSchema),
        defaultValues: {
            email: "",
        },
    });

    const onEmailSubscribe: SubmitHandler<EmailSubscribeInput> = (values) => {
        const email = values.email;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const anonymousId = window?.analytics?.user()?.anonymousId();
        const location = placement + ":" + pageLocation.pathname;

        analytics.track("Subscribe Clicked", {
            email,
            emailListId,
            location,
        });

        emailSubscribeMutation({
            variables: { email, anonymousId, emailListId, location },
        })
            .then(() => {
                setShowError(false);
                setShowSuccess(true);
            })
            .catch(() => {
                // error is surfaced through "saveError"
                setShowSuccess(false);
                setShowError(true);
            });
    };

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setShowSuccess(false);
    };

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset();
        }
    }, [isSubmitSuccessful, reset]);

    return (
        <>
            <FormInputText
                name="email"
                label="Email"
                data-testid="emailField"
                control={control}
                placeholder="Your email address"
                autoComplete="email"
                className={classes.newsletterTextField}
            />
            <Button
                variant="primary"
                size="small"
                data-testid="emailSubscribeButton"
                onClick={handleSubmit(onEmailSubscribe)}
            >
                Subscribe
            </Button>
            <Snackbar
                open={showSuccess}
                autoHideDuration={6000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
                    You have successfully subscribed!
                </Alert>
            </Snackbar>
        </>
    );
};
